import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { get } from 'lodash'
import { ArrowUp, ChevronDown, ChevronUp } from 'lucide-react'

import CurrencyInput from '../../../../components/CurrencyInput'
import ConfirmationModal from '../../../../components/ModalConfirmation'
import ReviewSummary from '../components/ReviewSummary'
import { orderItemCardId } from '../constants'
import { useOrderItemsModule } from '../contexts/OrderItemsModuleContext'
import type { OrderCoreSchemaType } from '../orderCore.schema'
import type { ReviewClosingVariantLine } from '../types'
import { computeReviewTotals, formatCurrency } from '../utils/reviewTotals'
import { SummaryLabel } from './Review.style'
import {
  AdjustLink,
  Breakdown,
  BreakdownDesc,
  BreakdownFormula,
  BreakdownIndex,
  BreakdownValue,
  BreakdownRow,
  ColHead,
  DistributeButton,
  EmptyState,
  ExpandButton,
  FechamentoContainer,
  FechamentoHeader,
  GradeBlock,
  GradeFormula,
  GradeRow,
  GradeTitle,
  GradeValue,
  GradeVariant,
  GradeWarning,
  HeaderPriceBlock,
  HeaderPriceRow,
  ItemName,
  ItemRow,
  ItemRowCard,
  Num,
  PriceFact,
  PriceFacts,
  RowsList,
  TableScroll,
  TypeChip,
} from './ReviewClosing.style'

const round2 = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100

const gradeMismatch = (variants: ReviewClosingVariantLine[], subtotal: number): boolean => {
  const somaGrade = variants.reduce((sum, variant) => sum + variant.subtotal, 0)
  return Math.abs(round2(somaGrade) - round2(subtotal)) > 0.01
}

/**
 * Fechamento da venda: preço do pedido, tabela por item com a composição de cada preço e o rateio.
 * As linhas vêm do módulo de itens porque o estado deles é do tenant; o núcleo soma e desenha.
 */
const ReviewClosing: React.FC = () => {
  const [showRateioConfirm, setShowRateioConfirm] = useState(false)
  const [openIndexes, setOpenIndexes] = useState<Set<number>>(new Set())
  const { control, setValue, watch, getValues, formState: { errors } } = useFormContext<OrderCoreSchemaType>()
  const { closingRows, closingBreakdown, distributePrice, totals, navigation } = useOrderItemsModule()

  const sellingPriceOrder = watch('sellingPrice')
  const review = computeReviewTotals(closingRows)
  const somaAtual = round2(review.effectiveTotal)

  const prevSomaRef = useRef<number | null>(null)
  useEffect(() => {
    const prev = prevSomaRef.current
    prevSomaRef.current = somaAtual
    if (somaAtual <= 0) return
    const atual = round2(Number(getValues('sellingPrice')) || 0)
    const estavaSincronizado = atual === 0 || (prev != null && Math.abs(atual - prev) < 0.005)
    if (estavaSincronizado && Math.abs(atual - somaAtual) >= 0.005) {
      setValue('sellingPrice', somaAtual, { shouldDirty: true, shouldValidate: false })
    }
  }, [somaAtual, getValues, setValue])

  const mostrarDistribuir = totals.canDistribute
    && !!sellingPriceOrder && Number(sellingPriceOrder) > 0
    && Math.abs(Number(sellingPriceOrder) - somaAtual) > 0.01
    && review.eligibleForRateio > 0

  const aplicarRateio = useCallback(() => {
    distributePrice(Number(getValues('sellingPrice') || 0))
  }, [distributePrice, getValues])

  const handleDistribuirClick = useCallback(() => {
    if (closingRows.some((row) => row.sellingPrice != null)) {
      setShowRateioConfirm(true)
      return
    }
    aplicarRateio()
  }, [closingRows, aplicarRateio])

  const goToItem = (index: number) => {
    navigation.onSelectItem(index)
    setTimeout(() => {
      document.getElementById(orderItemCardId(index))?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 50)
  }

  return (
    <>
      <FechamentoContainer>
        <FechamentoHeader>
          <ReviewSummary />

          <HeaderPriceBlock>
            <SummaryLabel as="label" htmlFor="sellingPrice">Preço de Venda do Pedido</SummaryLabel>
            <HeaderPriceRow>
              <Controller
                name="sellingPrice"
                control={control}
                render={({ field }) => (
                  <CurrencyInput
                    id="sellingPrice"
                    name="sellingPrice"
                    value={field.value || 0}
                    onValueChange={(value, name, values) => {
                      field.onChange(values?.float || undefined)
                    }}
                    placeholder="R$ 0,00"
                    hasError={!!get(errors, 'sellingPrice')}
                  />
                )}
              />
              {mostrarDistribuir && (
                <DistributeButton type="button" onClick={handleDistribuirClick}>
                  Distribuir pelos itens
                </DistributeButton>
              )}
            </HeaderPriceRow>
          </HeaderPriceBlock>
        </FechamentoHeader>

        {review.count === 0 ? (
          <EmptyState>Nenhum item para fechar ainda — os itens salvos acima aparecem aqui.</EmptyState>
        ) : (
          <TableScroll>
            <ColHead>
              <span>Item</span>
              <span>Peças</span>
              <span>Subtotal</span>
              <span />
            </ColHead>

            <RowsList>
              {closingRows.map((row) => {
                const calculado = row.registeredPrice
                const venda = row.sellingPrice ?? 0
                const efetivo = venda > 0 ? venda : calculado
                const desconto = calculado > 0 && venda > 0 ? ((calculado - venda) / calculado) * 100 : null
                const isOpen = openIndexes.has(row.index)
                const temGrade = !!row.variants?.length
                const breakdown = isOpen && !temGrade ? closingBreakdown(row.index) : []
                return (
                  <ItemRowCard key={row.index}>
                    <ItemRow>
                      <ItemName title={row.detail || row.name}>
                        <strong>{row.name}</strong>
                        {row.detail ? ` — ${row.detail}` : ''}
                      </ItemName>
                      <Num muted={!(Number(row.pieces) > 0)}>
                        {Number(row.pieces) > 0 ? Number(row.pieces) : '—'}
                      </Num>
                      <Num strong>{efetivo > 0 ? formatCurrency(efetivo) : '—'}</Num>
                      <ExpandButton
                        type="button"
                        isOpen={isOpen}
                        title={isOpen ? 'Recolher composição' : 'Ver composição do preço'}
                        onClick={() => setOpenIndexes((atual) => {
                          const proximo = new Set(atual)
                          if (isOpen) proximo.delete(row.index)
                          else proximo.add(row.index)
                          return proximo
                        })}
                      >
                        {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </ExpandButton>
                    </ItemRow>

                    {isOpen && (
                      <Breakdown>
                        <PriceFacts>
                          <PriceFact>
                            <b>Cadastrado</b>
                            <em>{calculado > 0 ? formatCurrency(calculado) : '—'}</em>
                          </PriceFact>
                          <PriceFact>
                            <b>Preço de venda</b>
                            <em>{venda > 0 ? formatCurrency(venda) : '—'}</em>
                          </PriceFact>
                          <PriceFact>
                            <b>{desconto != null && desconto < 0 ? 'Ajuste' : 'Desconto'}</b>
                            <em>
                              {desconto == null ? '—' : desconto >= 0
                                ? `${desconto.toFixed(1).replace('.', ',')}%`
                                : `+${Math.abs(desconto).toFixed(1).replace('.', ',')}%`}
                            </em>
                          </PriceFact>
                        </PriceFacts>
                        {!temGrade && breakdown.length === 0 ? (
                          <BreakdownRow>
                            <BreakdownDesc>Composição sem valores detalhados.</BreakdownDesc>
                          </BreakdownRow>
                        ) : breakdown.map((line, i) => (
                          <BreakdownRow key={i}>
                            <BreakdownIndex>{row.index + 1}.{i + 1}</BreakdownIndex>
                            <TypeChip>{line.chip}</TypeChip>
                            <BreakdownDesc title={line.desc}>{line.desc}</BreakdownDesc>
                            {line.formula && <BreakdownFormula>{line.formula}</BreakdownFormula>}
                            <BreakdownValue>{line.value != null && line.value > 0 ? formatCurrency(line.value) : '—'}</BreakdownValue>
                          </BreakdownRow>
                        ))}
                        {row.variants && row.variants.length > 0 && (
                          <GradeBlock>
                            <GradeTitle>Grade</GradeTitle>
                            {row.variants.map((variant) => (
                              <GradeRow key={`${variant.color}-${variant.size}`}>
                                <GradeVariant title={`${variant.color} · ${variant.size}`}>
                                  {variant.color} · {variant.size}
                                </GradeVariant>
                                <GradeFormula>
                                  {variant.quantity} un × {formatCurrency(variant.unitPrice)}
                                </GradeFormula>
                                <GradeValue>{formatCurrency(variant.subtotal)}</GradeValue>
                              </GradeRow>
                            ))}
                            {gradeMismatch(row.variants, efetivo) && (
                              <GradeWarning>
                                A soma da grade não fecha com o subtotal do item — confira as quantidades
                                antes de faturar.
                              </GradeWarning>
                            )}
                          </GradeBlock>
                        )}

                        <AdjustLink type="button" onClick={() => goToItem(row.index)}>
                          <ArrowUp size={13} /> Ajustar preço do item
                        </AdjustLink>
                      </Breakdown>
                    )}
                  </ItemRowCard>
                )
              })}
            </RowsList>
          </TableScroll>
        )}
      </FechamentoContainer>

      <ConfirmationModal
        isOpen={showRateioConfirm}
        title="Itens com Preço de Venda manual"
        message="Um ou mais itens já têm Preço de Venda definido manualmente — esses valores serão preservados e o restante do total será distribuído entre os demais itens. Deseja continuar?"
        actions={[
          { label: 'Cancelar', onClick: () => setShowRateioConfirm(false), variant: 'outline' },
          {
            label: 'Distribuir',
            onClick: () => {
              aplicarRateio()
              setShowRateioConfirm(false)
            },
            variant: 'primary',
          },
        ]}
      />
    </>
  )
}

export default ReviewClosing
